import { useEffect } from 'react';
import {
  ArrowRight,
  LogOut,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { AppLogo } from '@/components/layout/AppLogo';
import { useAuthStore } from '@/store/authStore';
import { onboardingService } from '@/services/onboardingService';

export default function NoOrganizationPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  useEffect(() => {
    void onboardingService
      .getAccessStatus()
      .then((status) => {
        if (status.state === 'approved') {
          navigate(ROUTES.DASHBOARD, { replace: true });
        } else if (status.state === 'pending_approval') {
          navigate(ROUTES.WAITING_APPROVAL, { replace: true });
        }
      })
      .catch(() => {
        // OnboardingGuard already fell back; stay on this page if API is unavailable.
      });
  }, [navigate]);

  const handleRegisterOrg = () => {
    navigate(ROUTES.ORG_REGISTRATION);
  };

  return (
    <div
      className="min-h-screen text-foreground flex flex-col"
      style={{ background: 'linear-gradient(135deg, var(--kibo-page-from) 0%, var(--kibo-page-to) 100%) !important' }}
    >
      {/* Top Bar */}
      <header className="fixed top-0 w-full z-50 flex justify-between items-center px-8 h-20 bg-card/80 backdrop-blur-xl border-b border-border/60">
        <div className="flex items-center gap-4">
          <AppLogo variant="full" alt="BlueB" className="h-10 w-auto" />
          <div className="h-6 w-px bg-border/50 mx-2 hidden sm:block"></div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-foreground">
            Organization Registration
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => logout()}
            className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 cursor-pointer"
          >
            <LogOut size={18} />
            Sign out
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 pt-24 px-4 pb-12 flex items-center justify-center">
        <div className="w-full max-w-[420px]">
          {/* Card matching the standard auth UI */}
          <div className="rounded-2xl border border-border/80 bg-card p-8 sm:p-9 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.06),0_10px_10px_-5px_rgba(0,0,0,0.02)]">
            {/* Centered Brand Logo */}
            <div className="flex justify-center mb-6">
              <AppLogo variant="full" alt="BlueB" className="h-14 w-auto" />
            </div>

            {/* Title & Subtitle */}
            <div className="text-center mb-6">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Get Started with BlueB
              </h2>
              <p className="text-sm text-muted-foreground mt-1.5">
                Register your organization to continue
              </p>
            </div>

            {/* Account & Status Form Fields */}
            <div className="space-y-4">
              <div className="space-y-1.5 text-left">
                <label className="text-sm font-medium text-foreground">
                  Username or email
                </label>
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value={user?.email || 'Authenticated User'}
                    className="w-full h-11 px-3.5 rounded-lg border border-input bg-muted/40 text-sm text-foreground focus:outline-none select-all"
                  />
                </div>
              </div>

              {/* Primary Action Button */}
              <button
                type="button"
                onClick={handleRegisterOrg}
                className="w-full h-11 mt-2 inline-flex items-center justify-center gap-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold shadow-sm transition-colors cursor-pointer"
              >
                <span>Register Organization</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border/70" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-3 text-muted-foreground">
                  or continue with
                </span>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="text-center text-sm text-muted-foreground">
              <span>Using a different account? </span>
              <button
                type="button"
                onClick={() => logout()}
                className="font-medium text-primary hover:underline cursor-pointer"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
